<template>
  <div class="step-page">
    <div class="step-header">
      <div class="step-badge">Projet : {{ projectName }}</div>
      <h1 class="step-title">Paramétrage de la simulation</h1>
    </div>

    <div class="step-content">
      <!-- Plage d'analyse — Matrice de résultats -->
      <div class="card-block" v-if="puissanceTotaleKwc > 0">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.plage ? 'false' : 'true'" @click="replie.plage = !replie.plage">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.plage ? '▸' : '▾' }}</span>Plage d'analyse — Matrice de résultats
          <span v-if="replie.plage" class="section-repliable__resume">{{ resumePlage }}</span>
          <span v-if="nbModifies.plage" class="badge-modifies">{{ libelleModifies(nbModifies.plage) }}</span>
        </button>
        <div v-show="!replie.plage">
        <p class="card-subtitle">
          5 puissances PV × 5 capacités batterie seront simulées dans ces plages.
          Chaque combinaison est calculée indépendamment et affichée dans la matrice de scénarios.
        </p>
        <div class="range-group">
          <div class="range-group__label">Puissance PV (kWc)</div>
          <div class="form-grid form-grid--4">
            <div class="form-group">
              <label class="form-label">Min</label>
              <input v-model.number="form.pvRangeMin" type="number" min="1" step="1" class="form-input" placeholder="Ex : 1" />
              <defaut-champ :valeur="form.pvRangeMin" :defaut="defauts.pvRangeMin" unite="kWc" @retablir="retablir('pvRangeMin')" />
            </div>
            <div class="form-group">
              <label class="form-label">Max</label>
              <input
                v-model.number="form.pvRangeMax"
                type="number"
                :min="form.pvRangeMin || 1"
                step="1"
                class="form-input"
                :placeholder="'Ex : ' + Math.round(puissanceTotaleKwc)"
              />
              <defaut-champ :valeur="form.pvRangeMax" :defaut="defauts.pvRangeMax" unite="kWc" @retablir="retablir('pvRangeMax')" />
            </div>
          </div>
          <p v-if="plagePvIncoherente" class="range-error">Le minimum doit être inférieur ou égal au maximum.</p>
        </div>
        <div class="range-group">
          <div class="range-group__label">Capacité batterie (kWh)</div>
          <div class="form-grid form-grid--4">
            <div class="form-group">
              <label class="form-label">Min</label>
              <input v-model.number="form.batRangeMin" type="number" min="0" step="1" class="form-input" placeholder="Ex : 5" />
              <defaut-champ :valeur="form.batRangeMin" :defaut="defauts.batRangeMin" unite="kWh" @retablir="retablir('batRangeMin')" />
            </div>
            <div class="form-group">
              <label class="form-label">Max</label>
              <input
                v-model.number="form.batRangeMax"
                type="number"
                :min="(form.batRangeMin || 0) + 1"
                step="1"
                class="form-input"
                placeholder="Ex : 25"
              />
              <defaut-champ :valeur="form.batRangeMax" :defaut="defauts.batRangeMax" unite="kWh" @retablir="retablir('batRangeMax')" />
            </div>
          </div>
          <p v-if="plageBatIncoherente" class="range-error">Le minimum doit être inférieur ou égal au maximum.</p>
          <p class="field-note">* La configuration sans batterie (0 kWh) est toujours incluse dans le calcul.</p>
        </div>
      </div>
      </div>

      <!-- Paramètres de calcul -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.calcul ? 'false' : 'true'" @click="replie.calcul = !replie.calcul">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.calcul ? '▸' : '▾' }}</span>Paramètres de calcul
          <span v-if="replie.calcul" class="section-repliable__resume">{{ resumeCalcul }}</span>
          <span v-if="nbModifies.calcul" class="badge-modifies">{{ libelleModifies(nbModifies.calcul) }}</span>
        </button>
        <div v-show="!replie.calcul">
        <div class="form-grid form-grid--3">
          <div class="form-group">
            <label class="form-label">Inflation de l'électricité (%/an)</label>
            <input v-model.number="form.inflationElec" type="number" min="0" max="50" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.inflationElec" :defaut="defauts.inflationElec" unite="%/an" @retablir="retablir('inflationElec')" />
          </div>
          <div class="form-group">
            <label class="form-label">Dégradation panneaux (%/an)</label>
            <input v-model.number="form.degradationPanneaux" type="number" min="0" max="5" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.degradationPanneaux" :defaut="defauts.degradationPanneaux" unite="%/an" @retablir="retablir('degradationPanneaux')" />
          </div>
          <div class="form-group">
            <label class="form-label">Dégradation batterie (%/an)</label>
            <input v-model.number="form.degradationBatterie" type="number" min="0" max="20" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.degradationBatterie" :defaut="defauts.degradationBatterie" unite="%/an" @retablir="retablir('degradationBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Rendement round-trip de la batterie (perte électrochimique à la charge). Valeur constructeur par défaut : 90 %.">Rendement batterie (%)</label>
            <input v-model.number="form.rendementBatterie" type="number" min="50" max="100" step="1" class="form-input" />
            <defaut-champ :valeur="form.rendementBatterie" :defaut="defauts.rendementBatterie" unite="%" @retablir="retablir('rendementBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Rendement de conversion CC/AC de l'onduleur hybride, appliqué uniquement à la décharge batterie (la charge PV→batterie reste en CC, sans perte de conversion). Valeur mesurée sur étude PV*SOL : ~98 %.">Rendement conversion CC/AC décharge (%)</label>
            <input v-model.number="form.rendementConversionBatterie" type="number" min="80" max="100" step="0.1" class="form-input" />
            <defaut-champ :valeur="form.rendementConversionBatterie" :defaut="defauts.rendementConversionBatterie" unite="%" @retablir="retablir('rendementConversionBatterie')" />
          </div>
          <div class="form-group">
            <label class="form-label" title="Part de la capacité nominale réellement utilisée (profondeur de décharge). Valeur courante : 90 %.">Capacité de batterie utilisée (%)</label>
            <input v-model.number="form.capaciteUtilisableBatterie" type="number" min="50" max="100" step="1" class="form-input" />
            <defaut-champ :valeur="form.capaciteUtilisableBatterie" :defaut="defauts.capaciteUtilisableBatterie" unite="%" @retablir="retablir('capaciteUtilisableBatterie')" />
          </div>
        </div>
      </div>
      </div>

      <!-- Hypothèses de coûts d'installation -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.couts ? 'false' : 'true'" @click="replie.couts = !replie.couts">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.couts ? '▸' : '▾' }}</span>Hypothèses de coûts d'installation
          <span v-if="replie.couts" class="section-repliable__resume">Grilles de prix PV et batterie</span>
          <span v-if="nbModifies.couts" class="badge-modifies">{{ libelleModifies(nbModifies.couts) }}</span>
        </button>
        <div v-show="!replie.couts">
        <p class="card-subtitle">
          Coûts d'installation utilisés pour estimer l'investissement et calculer la rentabilité du projet.
        </p>

        <div class="two-tables">
          <!-- Coût installation PV -->
          <div class="table-block">
            <div class="table-block__title">Coût Installation PV HT (€/Wc)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Puissance</th>
                  <th>Prix HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutInstallationPV" :key="'pv-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input
                        :value="(row.prix / 1000).toFixed(3)"
                        type="number" min="0" step="0.001"
                        class="form-input form-input--sm"
                        @input="row.prix = Math.round(parseFloat($event.target.value || 0) * 1000)"
                      />
                      <span class="unit">€/Wc</span>
                    </div>
                    <defaut-champ :valeur="row.prix / 1000" :defaut="prixDefaut('coutInstallationPV', idx, 1000)" unite="€/Wc" :decimales="3" @retablir="retablirTranche('coutInstallationPV', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Coût installation batterie -->
          <div class="table-block">
            <div class="table-block__title">Coût Installation Batterie HT (€/kWh)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Capacité</th>
                  <th>Prix HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutInstallationBatterie" :key="'bat-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="10" class="form-input form-input--sm" />
                      <span class="unit">€/kWh</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutInstallationBatterie', idx)" unite="€/kWh" @retablir="retablirTranche('coutInstallationBatterie', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
      </div>

      <!-- Hypothèses de coûts du câble onduleur ↔ TGBT (19/10/2026) : prix du fichier du BE, modifiables, repliées par défaut -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.cable ? 'false' : 'true'" @click="replie.cable = !replie.cable">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.cable ? '▸' : '▾' }}</span>Hypothèses de coûts du câble onduleur ↔ TGBT
          <span v-if="replie.cable" class="section-repliable__resume">Prix au mètre selon la section et raccords</span>
          <span v-if="nbModifies.cable" class="badge-modifies">{{ libelleModifies(nbModifies.cable) }}</span>
        </button>
        <div v-show="!replie.cable">
        <p class="card-subtitle">Case vide : prix non disponible.</p>
        <div class="table-block table-block--cable">
          <div class="table-block__title">Prix du câble HT (€/m)</div>
          <table class="params-table params-table--cable">
            <thead>
              <tr>
                <th>Section</th>
                <th>Alu triphasé</th>
                <th>Alu monophasé</th>
                <th>Cuivre triphasé</th>
                <th>Cuivre monophasé</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.prixCable" :key="'cable-' + idx">
                <td class="label-cell">{{ sectionTexte(row.section) }} mm²</td>
                <td v-for="col in COLONNES_CABLE" :key="col">
                  <input :value="row[col]" type="number" min="0" step="0.01" class="form-input form-input--sm"
                    :aria-label="'Prix ' + col + ' ' + sectionTexte(row.section) + ' mm²'"
                    @input="row[col] = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
                  <defaut-champ :valeur="row[col]" :defaut="prixCableDefaut(idx, col)" unite="€/m" @retablir="row[col] = prixCableDefaut(idx, col)" />
                </td>
              </tr>
            </tbody>
          </table>
          <table class="params-table params-table--raccords">
            <thead>
              <tr>
                <th>Raccord (câble aluminium jusqu'à 36 kVA)</th>
                <th>Prix HT pièce</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.prixRaccordsAlu" :key="'raccord-' + idx">
                <td class="label-cell">{{ row.label }} ({{ sectionTexte(row.sectionMin) }} à {{ sectionTexte(row.sectionMax) }} mm²)</td>
                <td>
                  <div class="input-unit">
                    <input v-model.number="row.prix" type="number" min="0" step="1" class="form-input form-input--sm" />
                    <span class="unit">€</span>
                  </div>
                  <defaut-champ :valeur="row.prix" :defaut="prixDefaut('prixRaccordsAlu', idx)" unite="€" @retablir="retablirTranche('prixRaccordsAlu', idx)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>

      <!-- Hypothèses de coûts d'exploitation (OPEX) : grilles, repliées par défaut (13/10/2026) -->
      <div class="card-block">
        <button type="button" class="card-block__title section-repliable" :aria-expanded="replie.opex ? 'false' : 'true'" @click="replie.opex = !replie.opex">
          <span class="section-repliable__chevron" aria-hidden="true">{{ replie.opex ? '▸' : '▾' }}</span>Hypothèses de coûts d'exploitation (OPEX)
          <span v-if="replie.opex" class="section-repliable__resume">Grilles et hausses de la maintenance et de l'assurance</span>
          <span v-if="nbModifies.opex" class="badge-modifies">{{ libelleModifies(nbModifies.opex) }}</span>
        </button>
        <div v-show="!replie.opex">
        <p class="card-subtitle">Coûts de maintenance et d'assurance annuels HT selon la puissance installée.</p>
        <!-- hausses dans les hypothèses ; interrupteurs dans « Paramétrage des coûts » (13/10/2026) -->
        <div class="opex-phrases">
          <p class="opex-phrase">
            <label for="hausse-maintenance-pct">Hausse de la maintenance :</label>
            <input id="hausse-maintenance-pct" v-model.number="form.hausseMaintenancePct" type="number" min="0" max="50" step="0.1" class="form-input trou" aria-label="Hausse de la maintenance (%)" />
            <span>% tous les</span>
            <input id="hausse-maintenance-periode" v-model.number="form.hausseMaintenancePeriode" type="number" min="1" max="20" step="1" class="form-input trou" aria-label="Période de la hausse (ans)" />
            <span>ans.</span>
            <defaut-champ :valeur="form.hausseMaintenancePct" :defaut="defauts.hausseMaintenancePct" unite="%" @retablir="retablir('hausseMaintenancePct')" />
            <defaut-champ :valeur="form.hausseMaintenancePeriode" :defaut="defauts.hausseMaintenancePeriode" unite="ans" @retablir="retablir('hausseMaintenancePeriode')" />
          </p>
          <p class="opex-phrase">
            <label for="inflation-assurance">Inflation de l'assurance :</label>
            <input id="inflation-assurance" v-model.number="form.inflationAssurance" type="number" min="0" max="20" step="0.1" class="form-input trou" aria-label="Inflation de l'assurance (%/an)" />
            <span>%/an.</span>
            <defaut-champ :valeur="form.inflationAssurance" :defaut="defauts.inflationAssurance" unite="%/an" @retablir="retablir('inflationAssurance')" />
          </p>
        </div>
        <div class="two-tables">
          <div class="table-block">
            <div class="table-block__title">Maintenance annuelle (forfait HT / an)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Tranche de puissance</th>
                  <th>Forfait annuel HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutMaintenance" :key="'maint-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="10" class="form-input form-input--sm" />
                      <span class="unit">€/an</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutMaintenance', idx)" unite="€/an" @retablir="retablirTranche('coutMaintenance', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-block">
            <div class="table-block__title">Assurance annuelle (forfait HT / an)</div>
            <table class="params-table">
              <thead>
                <tr>
                  <th>Tranche de puissance</th>
                  <th>Forfait annuel HT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in form.coutAssurance" :key="'assur-' + idx">
                  <td class="label-cell">{{ row.label }}</td>
                  <td>
                    <div class="input-unit">
                      <input v-model.number="row.prix" type="number" min="0" step="5" class="form-input form-input--sm" />
                      <span class="unit">€/an</span>
                    </div>
                    <defaut-champ :valeur="row.prix" :defaut="prixDefaut('coutAssurance', idx)" unite="€/an" @retablir="retablirTranche('coutAssurance', idx)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      <!-- Paramétrage des coûts (13/10/2026) : coûts d'installation, OPEX, financement et vente de surplus — déroulé -->
      <div class="card-block parametrage-couts">
        <div class="card-block__title">Paramétrage des coûts</div>

        <div class="sous-titre">Coûts d'installation</div>
        <!-- un coût par ligne, libellé centré sur la case (13/10/2026) -->
        <!-- ordre du 20/10/2026 : câble, datalogger (pro), raccordement, travaux -->
        <!-- câble onduleur ↔ TGBT (19/10/2026) : section et prix calculés pour chaque scénario (méthode du BE) -->
        <div class="cout-ligne enedis-field cable-field">
          <label class="cout-ligne__libelle" for="longueur-cable">Longueur du câble onduleur ↔ TGBT</label>
          <div class="input-unit">
            <input id="longueur-cable" :value="form.longueurCable" type="number" min="0" step="1" class="form-input form-input--sm" placeholder="non chiffré"
              @input="form.longueurCable = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
            <span class="unit">m</span>
          </div>
        </div>
        <!-- datalogger : professionnels seulement (12/10/2026), prix selon la puissance d'abonnement (13/10/2026) -->
        <div v-if="clientType !== 'Particulier'" class="cout-ligne enedis-field">
          <label class="cout-ligne__libelle" for="datalogger">Datalogger (HT)</label>
          <div class="input-unit">
            <!-- modifiable (13/10/2026) : vide = prix selon la puissance d'abonnement -->
            <input id="datalogger" :value="form.prixDatalogger != null ? form.prixDatalogger : prixDataloggerDefaut" type="number" min="0" step="100"
              class="form-input form-input--sm" @input="form.prixDatalogger = $event.target.value === '' ? null : Math.max(0, parseFloat($event.target.value) || 0)" />
            <span class="unit">€</span>
          </div>
        </div>
        <div class="cout-ligne enedis-field">
          <!-- raccordement ENEDIS : sans TVA (13/10/2026) -->
          <label class="cout-ligne__libelle" for="cout-raccordement">Coût de raccordement ENEDIS</label>
          <div class="input-unit">
            <input id="cout-raccordement" v-model.number="form.coutRaccordementEnedis" type="number" min="0" step="1" class="form-input form-input--sm" />
            <span class="unit">€</span>
          </div>
        </div>
        <div class="cout-ligne enedis-field travaux-field">
          <label class="cout-ligne__libelle" for="travaux-montant">Travaux supplémentaires (HT)</label>
          <div class="input-unit">
            <input id="travaux-montant" v-model.number="form.travauxSupplementaires" type="number" min="0" step="100" class="form-input form-input--sm" placeholder="0" />
            <span class="unit">€</span>
          </div>
          <input v-model="form.travauxSupplementairesLibelle" type="text" class="form-input form-input--sm travaux-libelle"
            placeholder="Nature des travaux (ex. renfort de charpente)" aria-label="Nature des travaux supplémentaires" />
        </div>
        <div class="sous-titre">Coûts OPEX</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecMaintenance" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Maintenance</span>
          </label>
          <span v-if="!form.avecMaintenance" class="opex-off">Non comptée dans la rentabilité.</span>
        </div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecAssurance" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Assurance</span>
          </label>
          <span v-if="!form.avecAssurance" class="opex-off">Non comptée dans la rentabilité.</span>
        </div>

        <div class="sous-titre">Financement</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="form.avecFinancement" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Financement</span>
          </label>
          <span v-if="!form.avecFinancement" class="opex-off">Sans emprunt.</span>
        </div>
        <div v-if="form.avecFinancement" class="financement-champs">
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="apport-pct">Apport (% du montant total)</label>
            <div class="input-unit">
              <input id="apport-pct" v-model.number="form.apportPct" type="number" min="0" max="100" step="1" class="form-input form-input--sm" />
              <span class="unit">%</span>
            </div>
          </div>
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="taux-emprunt">Taux annuel</label>
            <div class="input-unit">
              <input id="taux-emprunt" v-model.number="form.tauxEmprunt" type="number" min="0" max="30" step="0.1" class="form-input form-input--sm" />
              <span class="unit">%</span>
            </div>
          </div>
          <!-- durée au plus court (26/10/2026) : calculée pour chaque scénario ; la durée saisie ne sert que si aucune ne convient
               (interrupteur à droite de la durée, demande du 26/10/2026) -->
          <div class="cout-ligne">
            <label class="cout-ligne__libelle" for="duree-emprunt">{{ form.dureeEmpruntAuPlusCourt ? "Durée si non rentable sur 20 ans" : "Durée de l'emprunt" }}</label>
            <div class="input-unit">
              <input id="duree-emprunt" v-model.number="form.dureeEmprunt" type="number" min="1" max="30" step="1" class="form-input form-input--sm" />
              <span class="unit">ans</span>
            </div>
            <label class="toggle-row" title="Pour chaque scénario, l'emprunt se termine l'année où le projet devient rentable. La durée saisie ne sert que si le projet n'est pas rentable sur 20 ans.">
              <span class="toggle-switch">
                <input id="duree-au-plus-court" type="checkbox" v-model="form.dureeEmpruntAuPlusCourt" />
                <span class="toggle-slider"></span>
              </span>
              <span class="toggle-row__label">Au plus court</span>
            </label>
            <span v-if="form.dureeEmpruntAuPlusCourt" class="opex-off">Fin de l'emprunt l'année où le projet devient rentable.</span>
          </div>
        </div>

        <!-- vente de surplus en dernier, désactivée par défaut = zéro-injection (13/10/2026) -->
        <div class="sous-titre">Valorisation de l'énergie</div>
        <div class="cout-ligne">
          <label class="toggle-row">
            <span class="toggle-switch">
              <input type="checkbox" v-model="venteSurplus" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-row__label">Vente de surplus</span>
          </label>
          <span v-if="!venteSurplus" class="opex-off">Zéro-injection : le surplus n'est pas valorisé (onduleur bridé).</span>
        </div>
        <div v-if="venteSurplus" class="cout-ligne">
          <label class="cout-ligne__libelle" for="rachat-surplus">Rachat du surplus</label>
          <div class="input-unit">
            <input id="rachat-surplus" v-model.number="form.rachatSurplusUnitaire" type="number" min="0" step="0.001" class="form-input form-input--sm" />
            <span class="unit">€/kWh</span>
          </div>
        </div>
      </div>

      <!-- Info -->
      <div class="info-banner">
        <span class="info-banner__icon">ℹ️</span>
        <span>
          <strong>Rassurez-vous !</strong> Vous pourrez toujours adapter vos données et vos paramètres même après la génération du rapport.
        </span>
      </div>
    </div>

    <div class="step-footer">
      <button class="btn-back" @click="$emit('back')">Retour</button>
      <button class="btn-next" :disabled="plagesIncoherentes" @click="submit">Générer mon rapport →</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { buildInitialState } from '@/store/modules/wizard.module'
import DefautChamp, { estModifie } from '@/components/DefautChamp.vue'

// réglages de chaque section repliable, pour compter ceux qui s'écartent des valeurs par défaut (19/10/2026)
// colonnes de la grille du câble : aluminium / cuivre, 4 conducteurs (triphasé) / 2 (monophasé)
const COLONNES_CABLE = ['alu4', 'alu2', 'cuivre4', 'cuivre2']

const SECTIONS = {
  plage: ['pvRangeMin', 'pvRangeMax', 'batRangeMin', 'batRangeMax'],
  calcul: ['inflationElec', 'degradationPanneaux', 'degradationBatterie', 'rendementBatterie', 'rendementConversionBatterie', 'capaciteUtilisableBatterie'],
  couts: ['coutInstallationPV', 'coutInstallationBatterie'],
  cable: ['prixCable', 'prixRaccordsAlu'],
  opex: ['hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance', 'coutMaintenance', 'coutAssurance'],
}

export default {
  name: 'Step4Parametres',
  components: { DefautChamp },
  data() {
    return {
      formReady: false,
      COLONNES_CABLE,
      // sections repliées par défaut (12/10/2026) : réglages rarement modifiés
      replie: { plage: true, calcul: true, couts: true, cable: true, opex: true },
      form: {
        inflationElec: 3.5,
        capaciteUtilisableBatterie: 90,
        hausseMaintenancePct: 2,
        hausseMaintenancePeriode: 5,
        inflationAssurance: 1.5,
        degradationPanneaux: 0.4,
        degradationBatterie: 2,
        rendementBatterie: 90,
        rendementConversionBatterie: 98,
        coutInstallationPV: [],
        coutInstallationBatterie: [],
        rachatSurplusUnitaire: 0.011,
        zeroInjection: true,
        coutRaccordementEnedis: 0,
        travauxSupplementaires: 0,
        travauxSupplementairesLibelle: '',
        prixDatalogger: null,  // € HT saisi (13/10/2026) ; null = prix selon la puissance d'abonnement
        longueurCable: null,   // m (19/10/2026) ; null = câble non chiffré
        prixCable: [],
        prixRaccordsAlu: [],
        avecMaintenance: true,
        avecAssurance: true,
        coutMaintenance: [],
        coutAssurance: [],
        avecFinancement: false,
        apportPct: 20,
        tauxEmprunt: 4.5,
        dureeEmprunt: 10,
        dureeEmpruntAuPlusCourt: false,
        pvRangeMin: 1,
        pvRangeMax: null,
        batRangeMin: 5,
        batRangeMax: 25,
      },
    }
  },
  computed: {
    // valeurs par défaut d'une nouvelle étude (store), plage PV déduite de la puissance installable comme au montage
    defauts() {
      const d = buildInitialState()
      const pvMax = Math.round(this.puissanceTotaleKwc || 0) || null
      d.pvRangeMax = pvMax
      d.pvRangeMin = pvMax ? Math.max(1, Math.round(pvMax / 5)) : 1
      return d
    },
    nbModifies() {
      const n = {}
      Object.keys(SECTIONS).forEach(section => {
        n[section] = SECTIONS[section].reduce((total, cle) => {
          const v = this.form[cle]
          if (cle === 'prixCable') return total + v.reduce((t, row, idx) => t + COLONNES_CABLE.filter(col => estModifie(row[col], this.prixCableDefaut(idx, col))).length, 0)
          if (Array.isArray(v)) return total + v.filter((row, idx) => estModifie(row.prix, this.prixDefaut(cle, idx))).length
          return total + (estModifie(v, this.defauts[cle]) ? 1 : 0)
        }, 0)
      })
      return n
    },
    resumePlage() {
      const f = this.form
      return 'PV ' + (f.pvRangeMin || '—') + ' à ' + (f.pvRangeMax || '—') + ' kWc · batterie ' + (f.batRangeMin != null ? f.batRangeMin : '—') + ' à ' + (f.batRangeMax || '—') + ' kWh'
    },
    resumeCalcul() {
      const f = this.form
      const n = v => String(v).replace('.', ',')
      return 'Inflation ' + n(f.inflationElec) + ' %/an · dégradation PV ' + n(f.degradationPanneaux) + ' %/an · batterie utilisée à ' + f.capaciteUtilisableBatterie + ' %'
    },
    // B41 : une plage min > max ne doit pas partir au calcul
    plagePvIncoherente() {
      const f = this.form
      return f.pvRangeMin != null && f.pvRangeMax != null && f.pvRangeMin !== '' && f.pvRangeMax !== '' && f.pvRangeMin > f.pvRangeMax
    },
    plageBatIncoherente() {
      const f = this.form
      return f.batRangeMin != null && f.batRangeMax != null && f.batRangeMin !== '' && f.batRangeMax !== '' && f.batRangeMin > f.batRangeMax
    },
    plagesIncoherentes() { return this.plagePvIncoherente || this.plageBatIncoherente },
    // interrupteur « Vente de surplus » : désactivé = zéro-injection (13/10/2026)
    venteSurplus: {
      get() { return !this.form.zeroInjection },
      set(v) { this.form.zeroInjection = !v },
    },
    // même règle que le serveur (WizardService.prixDatalogger) : selon la puissance d'abonnement (13/10/2026)
    prixDataloggerDefaut() {
      return this.puissanceAbonnement != null && this.puissanceAbonnement > 36 ? 1500 : 1000
    },
    ...mapState('wizard', [
      'projectName', 'inflationElec', 'capaciteUtilisableBatterie', 'hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance',
      'degradationPanneaux', 'degradationBatterie', 'rendementBatterie', 'rendementConversionBatterie',
      'coutInstallationPV', 'coutInstallationBatterie',
      'rachatSurplusUnitaire', 'zeroInjection', 'coutRaccordementEnedis', 'travauxSupplementaires', 'travauxSupplementairesLibelle', 'prixDatalogger', 'avecMaintenance', 'avecAssurance', 'coutMaintenance', 'coutAssurance',
      'avecFinancement', 'apportPct', 'tauxEmprunt', 'dureeEmprunt', 'dureeEmpruntAuPlusCourt',
      'pvRangeMin', 'pvRangeMax', 'batRangeMin', 'batRangeMax', 'puissanceTotaleKwc',
      'clientType', 'puissanceAbonnement', 'longueurCable', 'prixCable', 'prixRaccordsAlu',
    ]),
    ...mapGetters('wizard', ['nomRapportDefault']),
  },
  watch: {
    form: {
      deep: true,
      handler() { if (this.formReady) this.SET_DIRTY(true) },
    },
  },
  mounted() {
    Object.assign(this.form, {
      inflationElec: this.inflationElec,
      capaciteUtilisableBatterie: this.capaciteUtilisableBatterie != null ? this.capaciteUtilisableBatterie : 90,
      hausseMaintenancePct: this.hausseMaintenancePct,
      hausseMaintenancePeriode: this.hausseMaintenancePeriode,
      inflationAssurance: this.inflationAssurance,
      degradationPanneaux: this.degradationPanneaux,
      degradationBatterie: this.degradationBatterie,
      rendementBatterie: this.rendementBatterie,
      rendementConversionBatterie: this.rendementConversionBatterie,
      coutInstallationPV: JSON.parse(JSON.stringify(this.coutInstallationPV)),
      coutInstallationBatterie: JSON.parse(JSON.stringify(this.coutInstallationBatterie)),
      rachatSurplusUnitaire: this.rachatSurplusUnitaire,
      zeroInjection: this.zeroInjection,
      coutRaccordementEnedis: this.coutRaccordementEnedis,
      travauxSupplementaires: this.travauxSupplementaires || 0,
      travauxSupplementairesLibelle: this.travauxSupplementairesLibelle || '',
      prixDatalogger: this.prixDatalogger != null ? this.prixDatalogger : null,
      longueurCable: this.longueurCable != null ? this.longueurCable : null,
      prixCable: JSON.parse(JSON.stringify(this.prixCable || this.defauts.prixCable)),
      prixRaccordsAlu: JSON.parse(JSON.stringify(this.prixRaccordsAlu || this.defauts.prixRaccordsAlu)),
      avecMaintenance: this.avecMaintenance,
      avecAssurance: this.avecAssurance !== false,
      coutMaintenance: JSON.parse(JSON.stringify(this.coutMaintenance)),
      coutAssurance: JSON.parse(JSON.stringify(this.coutAssurance)),
      avecFinancement: this.avecFinancement,
      apportPct: this.apportPct,
      tauxEmprunt: this.tauxEmprunt,
      dureeEmprunt: this.dureeEmprunt,
      dureeEmpruntAuPlusCourt: !!this.dureeEmpruntAuPlusCourt,
      batRangeMin: this.batRangeMin,
      batRangeMax: this.batRangeMax,
    })
    // pvRangeMax déduit de la puissance installée si non encore défini
    var pvMax = this.pvRangeMax || Math.round(this.puissanceTotaleKwc || 0) || null
    this.form.pvRangeMax = pvMax
    this.form.pvRangeMin = this.pvRangeMin !== null
      ? this.pvRangeMin
      : (pvMax ? Math.max(1, Math.round(pvMax / 5)) : 1)
    this.$nextTick(() => { this.formReady = true })
  },
  beforeDestroy() {
    this.SET_STEP4({
      inflationElec: this.form.inflationElec,
        capaciteUtilisableBatterie: this.form.capaciteUtilisableBatterie,
      hausseMaintenancePct: this.form.hausseMaintenancePct,
      hausseMaintenancePeriode: this.form.hausseMaintenancePeriode,
      inflationAssurance: this.form.inflationAssurance,
      degradationPanneaux: this.form.degradationPanneaux,
      degradationBatterie: this.form.degradationBatterie,
      rendementBatterie: this.form.rendementBatterie,
      rendementConversionBatterie: this.form.rendementConversionBatterie,
      coutInstallationPV: JSON.parse(JSON.stringify(this.form.coutInstallationPV)),
      coutInstallationBatterie: JSON.parse(JSON.stringify(this.form.coutInstallationBatterie)),
      rachatSurplusUnitaire: this.form.rachatSurplusUnitaire,
      zeroInjection: this.form.zeroInjection,
      coutRaccordementEnedis: this.form.coutRaccordementEnedis,
      travauxSupplementaires: this.form.travauxSupplementaires || 0,
      travauxSupplementairesLibelle: this.form.travauxSupplementairesLibelle || '',
        prixDatalogger: this.form.prixDatalogger,
        longueurCable: this.form.longueurCable,
        prixCable: JSON.parse(JSON.stringify(this.form.prixCable)),
        prixRaccordsAlu: JSON.parse(JSON.stringify(this.form.prixRaccordsAlu)),
      avecMaintenance: this.form.avecMaintenance,
      avecAssurance: this.form.avecAssurance,
      coutMaintenance: JSON.parse(JSON.stringify(this.form.coutMaintenance)),
      coutAssurance: JSON.parse(JSON.stringify(this.form.coutAssurance)),
      avecFinancement: this.form.avecFinancement,
      apportPct: this.form.apportPct,
      tauxEmprunt: this.form.tauxEmprunt,
      dureeEmprunt: this.form.dureeEmprunt,
      dureeEmpruntAuPlusCourt: this.form.dureeEmpruntAuPlusCourt,
      pvRangeMin: this.form.pvRangeMin,
      pvRangeMax: this.form.pvRangeMax,
      batRangeMin: this.form.batRangeMin,
      batRangeMax: this.form.batRangeMax,
    })
  },
  methods: {
    ...mapMutations('wizard', ['SET_DIRTY', 'SET_STEP4']),
    retablir(cle) { this.form[cle] = this.defauts[cle] },
    // prix par défaut d'une ligne de grille (même libellé de tranche), divisé par « echelle » pour l'affichage (€/Wc)
    prixDefaut(grille, idx, echelle) {
      const row = this.form[grille][idx]
      const defaut = (this.defauts[grille] || []).find(t => t.label === row.label)
      if (!defaut) return null
      return echelle ? defaut.prix / echelle : defaut.prix
    },
    retablirTranche(grille, idx) {
      const p = this.prixDefaut(grille, idx)
      if (p != null) this.form[grille][idx].prix = p
    },
    // prix par défaut d'une case de la grille du câble (même section)
    prixCableDefaut(idx, col) {
      const row = this.form.prixCable[idx]
      const defaut = (this.defauts.prixCable || []).find(t => t.section === row.section)
      return defaut ? defaut[col] : null
    },
    sectionTexte(v) { return String(v).replace('.', ',') },
    libelleModifies(n) { return n + (n > 1 ? ' réglages modifiés' : ' réglage modifié') },
    submit() {
      if (this.plagesIncoherentes) return
      this.SET_STEP4({
        inflationElec: this.form.inflationElec,
        capaciteUtilisableBatterie: this.form.capaciteUtilisableBatterie,
        hausseMaintenancePct: this.form.hausseMaintenancePct,
      hausseMaintenancePeriode: this.form.hausseMaintenancePeriode,
      inflationAssurance: this.form.inflationAssurance,
        degradationPanneaux: this.form.degradationPanneaux,
        degradationBatterie: this.form.degradationBatterie,
        rendementBatterie: this.form.rendementBatterie,
        rendementConversionBatterie: this.form.rendementConversionBatterie,
        coutInstallationPV: JSON.parse(JSON.stringify(this.form.coutInstallationPV)),
        coutInstallationBatterie: JSON.parse(JSON.stringify(this.form.coutInstallationBatterie)),
        rachatSurplusUnitaire: this.form.rachatSurplusUnitaire,
        zeroInjection: this.form.zeroInjection,
        coutRaccordementEnedis: this.form.coutRaccordementEnedis || 0,
        travauxSupplementaires: this.form.travauxSupplementaires || 0,
        travauxSupplementairesLibelle: this.form.travauxSupplementairesLibelle || '',
        prixDatalogger: this.form.prixDatalogger,
        longueurCable: this.form.longueurCable,
        prixCable: JSON.parse(JSON.stringify(this.form.prixCable)),
        prixRaccordsAlu: JSON.parse(JSON.stringify(this.form.prixRaccordsAlu)),
        avecMaintenance: this.form.avecMaintenance,
        avecAssurance: this.form.avecAssurance,
        coutMaintenance: JSON.parse(JSON.stringify(this.form.coutMaintenance)),
        coutAssurance: JSON.parse(JSON.stringify(this.form.coutAssurance)),
        avecFinancement: this.form.avecFinancement,
        apportPct: this.form.apportPct,
        tauxEmprunt: this.form.tauxEmprunt,
        dureeEmprunt: this.form.dureeEmprunt,
        dureeEmpruntAuPlusCourt: this.form.dureeEmpruntAuPlusCourt,
        pvRangeMin: this.form.pvRangeMin || 1,
        pvRangeMax: this.form.pvRangeMax || Math.round(this.puissanceTotaleKwc || 0) || 1,
        batRangeMin: this.form.batRangeMin !== null && this.form.batRangeMin !== undefined ? this.form.batRangeMin : 5,
        batRangeMax: this.form.batRangeMax || 25,
      })
      this.$emit('next')
    },
  },
}
</script>

<style scoped>
/* câble onduleur ↔ TGBT (19/10/2026) */
.params-table--cable th, .params-table--cable td { padding-right: 24px; }
.params-table--cable .form-input--sm { width: 80px; }
.params-table--raccords { margin-top: 12px; }

/* réglage modifié : case surlignée, valeur par défaut affichée à côté (19/10/2026) ; pas dans « Paramétrage des coûts » (choix de chaque étude) */
.form-group:has(.defaut-champ) > .form-input, td:has(.defaut-champ) .form-input { background: #fff8e6; border-color: #e0c27a; }
.form-group .defaut-champ { display: flex; }
td .defaut-champ { display: flex; }
.opex-phrase .defaut-champ { margin-left: 8px; }
.badge-modifies { margin-left: 10px; padding: 1px 8px; border-radius: 10px; background: #fff3d6; color: #8a5a00; font-size: 12px; font-weight: 600; }

.step-page {
  font-family: 'Raleway', sans-serif;
  background: #f5f5f5;
  min-height: calc(100vh - 140px);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }

.step-badge {
  display: inline-block;
  background: #f5c518;
  color: #222;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.step-content { display: flex; flex-direction: column; gap: 20px; }

.card-block {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-block__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
}

.card-block__title {
  font-size: 16px;
  font-weight: 700;
  color: #222;
  display: inline-block;
}

/* titre seul (sans .card-block__header) : garde sa bordure basse */
.card-block > .card-block__title {
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-row__label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }

.toggle-slider {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: #ddd; border-radius: 24px; cursor: pointer;
  transition: background 0.2s;
}

.toggle-switch input:checked + .toggle-slider { background: #f5c518; }

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px; width: 18px;
  left: 3px; bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider::before { transform: translateX(20px); }

.financement-off-hint {
  font-size: 13px;
  color: #aaa;
  font-style: italic;
  margin: 0;
  padding: 8px 0;
}

.card-subtitle {
  font-size: 13px;
  color: #888;
  margin-bottom: 20px;
  margin-top: -10px;
}

.form-grid {
  display: grid;
  gap: 16px;
}

.form-grid--3 { grid-template-columns: repeat(3, 1fr); }
.form-grid--4 { grid-template-columns: repeat(4, 1fr); }

.form-group { display: flex; flex-direction: column; gap: 4px; }

.form-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #222;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-input:focus { border-color: #f5c518; box-shadow: 0 0 0 2px rgba(245,197,24,0.2); }

.form-input--sm { padding: 6px 10px; font-size: 13px; }

/* Tables */
/* tableaux à la largeur de leur contenu, côte à côte (19/10/2026 : les prix étaient loin des libellés) */
.two-tables {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px 64px;
}

.table-block__title {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.table-block__subtitle {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin-top: 20px;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.enedis-field {
  display: flex;
  align-items: center;
  gap: 16px;
}

.params-table {
  width: auto;
  border-collapse: collapse;
}

.params-table th {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-align: left;
  padding: 6px 8px;
  border-bottom: 2px solid #f0f0f0;
}

.params-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
}

.label-cell {
  padding-right: 32px !important;
  font-size: 13px;
  color: #444;
  font-weight: 600;
}

.input-unit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-unit .form-input { max-width: 100px; }

.unit {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.field-note {
  font-size: 12px;
  color: #6b7280; /* plus lisible (13/10/2026) */
  margin-top: 6px;
  line-height: 1.5;
}





.range-group {
  margin-bottom: 20px;
}

.range-group:last-child { margin-bottom: 0; }

.range-group__label {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin-bottom: 10px;
  padding-left: 2px;
}

/* Info banner */
.info-banner {
  background: #fffde7;
  border: 1px solid #f5c518;
  border-radius: 10px;
  padding: 14px 20px;
  font-size: 14px;
  color: #444;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-banner__icon { font-size: 18px; flex-shrink: 0; }

/* Footer */
.step-footer { display: flex; gap: 12px; padding-top: 8px; justify-content: center; }

.btn-back {
  padding: 12px 32px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
}

.btn-back:hover { border-color: #999; }

.btn-next {
  padding: 12px 40px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-next:hover { background: #e6b800; }
.btn-next:disabled { opacity: 0.5; cursor: not-allowed; }
.travaux-libelle { margin-top: 6px; }
.range-error { color: #c62828; font-size: 13px; margin: 6px 0 0; }
.opex-phrases { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.opex-phrase { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 0; font-size: 14px; }
.opex-phrase label { margin: 0; font-weight: 600; }
.form-input.trou { width: 80px; text-align: center; padding: 4px 8px; }
.section-repliable {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px 10px; width: 100%;
  background: none; border: 0; border-bottom: 2px solid #f5c518; padding: 0 0 10px; margin-bottom: 0;
  text-align: left; cursor: pointer; font: inherit; font-size: 16px; font-weight: 700; color: #222;
}
.card-block > .section-repliable { display: flex; margin-bottom: 18px; }
.card-block > .section-repliable[aria-expanded="false"] { margin-bottom: 0; border-bottom-color: transparent; padding-bottom: 0; }
.section-repliable:focus-visible { outline: 2px solid #f5c518; outline-offset: 4px; }
.section-repliable__chevron { width: 14px; color: #b08d00; }
.section-repliable__resume { font-size: 13px; font-weight: 500; color: #888; }
.cout-ligne { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 16px; min-height: 38px; margin-bottom: 10px; }
.cout-ligne__libelle { width: 260px; margin: 0; font-size: 14px; font-weight: 600; color: #444; }
.cout-ligne .input-unit { width: 180px; }
.cout-ligne .toggle-row { min-width: 150px; }
.cout-ligne .travaux-libelle { flex: 1; min-width: 220px; margin: 0; }
.sous-titre { font-size: 13px; font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: .04em; margin: 4px 0 12px; }
.parametrage-couts .sous-titre ~ .sous-titre { margin-top: 18px; padding-top: 16px; border-top: 1px solid #eee; }
.opex-off { margin: 0; font-size: 13px; color: #999; }
</style>
